import { getAppointmentBookingAlternativeUrlFormatter } from '../FeatureConfigs/Appointments/utils.js';
import { getClickAndReserveFeatureConfig } from '../FeatureConfigs/ClickAndReserve/utils.js';

const featureToggleLookup = {
    AAB: 'isAABEnabled',
    AAB_ALTERNATIVE_STORE_AVAILABILITY: 'isAabAlternativeStoreAvailabilityEnabled',
    AAB_WITH_GROUPED_APPOINTMENT_TYPES: 'isAabWithGroupedAppointmentTypesEnabled',
    APPOINTMENT_BOOKING_TYPE_WITH_FREE_INDICATION: 'isAppointmentBookingTypeWithFreeIndication',
    CONTACTLENS_STOCK: 'isContactLensStockEnabled',
    CONTACTLENS: 'isContactLensEnabled',
    CONTACTLENS_PDP_V2: 'isContactLensPDPv2Enabled',
    CART_DISCOUNTS: 'isCartDiscountsEnabled',
    CLEAR_CART_ON_ALL_ERRORS: 'isClearCartOnAllErrorsEnabled',
    DOWNLOAD_QUOTATION: 'isDownloadQuotationEnabled',
    GPS_STORES: 'isGpsStoresEnabled',
    LEGACY_ORDER_SUPPLIER_EAN: 'isLegacyOrderSupplierEanEnabled',
    MONETATE_PRODUCT_DATA: 'isMonetateProductDataEnabled',
    MULTI_COUNTRY_LOCATIONS: 'isMultiCountryLocationsEnabled',
    MULTILINGUAL: 'isMultilingualEnabled',
    MYACCOUNT: 'isMyAccountEnabled',
    OMNI_VOUCHER: 'isOmniVoucherEnabled',
    PERSISTENT_QUERY_DISABLED: 'isPersistentQueryDisabled',
    PRESCRIPTION_FILE: 'isPrescriptionFileEnabled',
    PRESCRIPTION_RECOGNITION: 'isPrescriptionRecognitionEnabled',
    // Note: PRICE_PER_LITER name does not match the internal FeatureSet name, this is intentional.
    PRICE_PER_LITER: 'isPricePerUnitEnabled',
    PRESCRIPTION_GLASSES_CONFIGURATOR_VALIDATION_STEP: 'isPrescriptionGlassesConfiguratorValidationStepEnabled',
    PRESCRIPTION_GLASSES_REORDER: 'isPrescriptionGlassesReorderEnabled',
    PRESCRIPTION_GLASSES: 'isPrescriptionGlassesEnabled',
    PRESCRIPTION_GLASSES_QUANTITY_SELECTOR_DISABLED: 'isPrescriptionGlassesQuantitySelectorDisabled',
    PRESCRIPTION_GLASSES_DUPLICATE_BUTTON_DISABLED: 'isPrescriptionGlassesDuplicateButtonDisabled',
    BEST_PRICE: 'isBestPriceEnabled',
    REVIEWS: 'isReviewsEnabled',
    REORDER_BUTTON: 'isReorderButtonEnabled',
    SOLUTIONS_PDP_V2: 'isSolutionsPDPv2Enabled',
    STORE_SEARCH_SHOW_EMAIL: 'isStoreSearchEmailEnabled',
    STORE_SEARCH_SHOW_PHONE: 'isStoreSearchPhoneEnabled',
    STORE_DETAILS_SHOW_EMAIL: 'isStoreDetailsEmailEnabled',
    STORE_DETAILS_SHOW_PHONE: 'isStoreDetailsPhoneEnabled',
    STORE_DETAILS_BOOK_APPOINTMENT_BUTTON_DISABLED: 'isStoreDetailsBookAppointmentButtonDisabled',
    STORE_FILTERS: 'isStoreFiltersEnabled',
    STORE_FILTERS_STORES: 'isStoreFiltersStoresEnabled',
    STORE_PICKUP_SHOW_EMAIL: 'isStorePickupEmailEnabled',
    STORE_PICKUP_SHOW_PHONE: 'isStorePickupPhoneEnabled',
    STORE_RATINGS: 'isStoreRatingsEnabled',
    STORES_NEARBY: 'isStoresNearbyEnabled',
    STORES_PLACE_PREDICTIONS: 'isStoresPlacePredictionsEnabled',
    STORES_DISABLE_AAB_STORE_VALUE_AAB_BUTTON: 'isStoreValueInAabButtonDisabled',
    DELIVERY_FULL_DATE: 'isDeliveryFullDateEnabled',
    CART_RECOMMENDATIONS: 'isCartRecommendationsEnabled',
    MINI_CART_RECOMMENDATIONS: 'isMiniCartRecommendationsEnabled',
    VTO: 'isVtoEnabled',
    APPLE_PAY_EXPRESS_CHECKOUT: 'isApplePayExpressCheckoutEnabled',
    LOYALTY_POINTS: 'isLoyaltyPointsEnabled',
    CLICK_AND_RESERVE: 'isClickAndReserveEnabled',
    ENABLE_SOCIAL_LOGIN_FOR_CHECKOUT: 'isSocialLoginForCheckoutEnabled',
    LIME: 'isLimeEnabled',
    PRESCRIPTION_INFO: 'isPrescriptionInfoEnabled',
    WISH_LIST: 'isWishlistEnabled',
    TRY_IN_STORE: 'isTryInStoreEnabled',
    RETURNS: 'isReturnsEnabled',
    ORDER_WORKFLOW_STATUS: 'isOrderWorkflowStatusEnabled',
    LINE_ITEM_DELIVERY_ESTIMATION_DISABLED: 'isLineItemDeliveryEstimationDisabled',
    PRICE_DISCLAIMER: 'isPriceDisclaimerEnabled',
    VTO_TRANSITIONS: 'isVtoTransitionsEnabled',
    PUPILLARY_DISTANCE_FILE_OPTIONAL: 'isPupillaryDistanceFileOptional',
    RX_AXIS_CYLINDER_OPTIONAL: 'isRxAxisCylinderOptional',
    BACK_IN_STOCK: 'isBackInStockEnabled',
    PDP_PROMOTE_INSTALLMENTS: 'isPdpPromoteInstallmentsEnabled',
    WORDLIFT_SCRIPT: 'isWordLiftScriptEnabled',
    LOGGED_IN_EXPRESS_CHECKOUT: 'isLoggedInExpressCheckoutEnabled',
    IS_CART_EXPANDED: 'isCartExpanded',
    RX_PRICE_FROM: 'isRxPriceFromEnabled',
    CONFIGURATOR_RX_PRICE_FROM: 'isConfiguratorRxPriceFromEnabled',
    ALLOW_ALTERNATIVE_SHIPPING_STATE: 'allowAlternativeShippingState',
    PDP_MODEL_CODE_DISABLED: 'isPdpModelCodeDisabled',
    WORDLIFT_RELATED_LINKS: 'isWordLiftRelatedLinksEnabled',
    DISABLE_ACCESSORIES_PDP_QUANTITY_SELECTOR: 'isAccessoriesPdpQuantitySelectorDisabled',
    PDP_BLOOMREACH_PATH_OVERRIDE: 'isPdpBloomreachPathOverrideEnabled',
    PLP_FROM_PRICE_MESSAGE_ENABLED: 'isPlpFromPriceMessageEnabled',
    CART_DELIVERY_INDICATOR: 'isCartDeliveryIndicatorEnabled',
    DYNAMIC_LINE_ITEM_DELIVERY_ESTIMATION: 'isDynamicLineItemDeliveryEstimationEnabled',
    MANUFACTURER_DETAILS_ON_PDP: 'isManufacturerDetailsOnPdpEnabled',
    DISTINCT_GTM_SNIPPETS: 'isDistinctGTMSnippetsEnabled',
    CASH_ON_DELIVERY: 'isCashOnDeliveryEnabled'
};
const createFeatureSet = (enabledEnvFeatures)=>{
    const enabledFeaturesSet = new Set(enabledEnvFeatures);
    const completeFeatureSet = {};
    Object.entries(featureToggleLookup).forEach(([key, mappedKey])=>{
        completeFeatureSet[mappedKey] = enabledFeaturesSet.has(key);
    });
    return completeFeatureSet;
};
/**
 * This function parses comma separated strings to arrays via the regular `split()`.
 * The benefit of this function is that it catches errors and removes empty values.
 */ const parseCommaSeparatedString = (string)=>{
    try {
        const array = string === null || string === void 0 ? void 0 : string.split(',') // Split comma separated string
        .map((value)=>value.trim()
        ).filter((value)=>value
        ) // Remove empty values
        ;
        return (array === null || array === void 0 ? void 0 : array.length) ? array : undefined;
    } catch (error) {
        console.error(`Error parsing feature config string '${string}': ${error}`);
    }
};
const parseIntBaseTen = (value)=>value !== undefined ? parseInt(value, 10) : undefined
;
var _SURVEY_LINK, _BACK_IN_STOCK_NOTIFICATION_PERIOD_DAYS;
const createFeatureConfigs = (configs)=>{
    var ref, ref1;
    return {
        datePickerDateFormat: configs.DATE_PICKER_DATE_FORMAT || 'MMMM d, yyyy',
        prescriptionTypesOrder: parseCommaSeparatedString(configs.PRESCRIPTION_TYPES_ORDER),
        prescriptionList: parseCommaSeparatedString(configs.PRESCRIPTION_LIST),
        expressDefaultShippingMethod: configs.EXPRESS_DEFAULT_SHIPPING_METHOD,
        maxSearchStoresResults: parseIntBaseTen(configs.MAX_SEARCH_STORES_RESULTS),
        maxNearbyStoresDistance: parseIntBaseTen(configs.MAX_NEARBY_STORES_DISTANCE_METERS),
        appointmentBookingUrlPaddedLength: parseIntBaseTen(configs.APPOINTMENT_BOOKING_URL_PADDED_LENGTH),
        appointmentBookingAvailabiltyCalendarWeekCount: parseIntBaseTen(configs.APPOINTMENT_BOOKING_AVAILABILITY_CALENDAR_WEEK_COUNT),
        appointmentBookingVirtualAppointmentTypeId: configs === null || configs === void 0 ? void 0 : configs.APPOINTMENT_BOOKING_VIRTUAL_APPOINTMENT_TYPE_ID,
        appointmentBookingVirtualAppointmentStoreCode: configs.APPOINTMENT_BOOKING_VIRTUAL_APPOINTMENT_STORE_CODE,
        appointmentBookingSurveyLinks: configs.APPOINTMENT_BOOKING_SURVEY_LINKS !== undefined ? Array.from(configs.APPOINTMENT_BOOKING_SURVEY_LINKS.matchAll(/([a-zA-Z-0-9]+)>([^\^]+)/g), ([, appointmentId, url])=>[
                appointmentId,
                url
            ]
        ) : undefined,
        appointmentBookingSurveyLinksStoreCodes: parseCommaSeparatedString(configs.APPOINTMENT_BOOKING_SURVEY_LINKS_STORE_CODES),
        surveyLink: (_SURVEY_LINK = configs.SURVEY_LINK) !== null && _SURVEY_LINK !== void 0 ? _SURVEY_LINK : undefined,
        appointmentBookingAlternativeUrl: getAppointmentBookingAlternativeUrlFormatter(configs.APPOINTMENT_BOOKING_ALTERNATIVE_URL),
        appointmentBookingAlternativeUrlStores: parseCommaSeparatedString(configs.APPOINTMENT_BOOKING_ALTERNATIVE_URL_STORES),
        storeDistanceConfig: (ref = parseCommaSeparatedString(configs.STORE_DISTANCE_CONFIG)) === null || ref === void 0 ? void 0 : ref.reduce((indexed, distanceProp)=>{
            indexed[distanceProp] = true;
            return indexed;
        }, {}),
        googleDynamicMapsMapId: configs.GOOGLE_DYNAMIC_MAPS_MAP_ID,
        clickAndReserve: getClickAndReserveFeatureConfig(configs.CLICK_AND_RESERVE),
        additionalLoginProviders: parseCommaSeparatedString(configs.ADDITIONAL_LOGIN_PROVIDERS),
        globalContactFormUrl: configs.GLOBAL_CONTACT_FORM_URL,
        ogSiteName: configs.OG_SITE_NAME,
        unitPriceFixedDecimalsConfig: (ref1 = parseCommaSeparatedString(configs.UNIT_PRICE_FIXED_DECIMALS)) === null || ref1 === void 0 ? void 0 : ref1.reduce((indexed, pageProp)=>{
            indexed[pageProp] = true;
            return indexed;
        }, {}),
        backInStockNotificationPeriodDays: (_BACK_IN_STOCK_NOTIFICATION_PERIOD_DAYS = configs.BACK_IN_STOCK_NOTIFICATION_PERIOD_DAYS) !== null && _BACK_IN_STOCK_NOTIFICATION_PERIOD_DAYS !== void 0 ? _BACK_IN_STOCK_NOTIFICATION_PERIOD_DAYS : '30',
        algoliaIndexV2EnabledProductTypes: parseCommaSeparatedString(configs.ALGOLIA_INDEX_V2_ENABLED_PRODUCT_TYPES),
        alternativeShippingRegex: configs.ALTERNATIVE_SHIPPING_REGEX,
        priceSegmentQueryKey: configs.PRICE_SEGMENT_QUERY_KEY,
        priceSegmentCookieAgeInHours: configs.PRICE_SEGMENT_COOKIE_AGE_IN_HOURS
    };
};
const createServerFeatureConfigs = (configs)=>({
        priceSegmentAllowedReferrers: parseCommaSeparatedString(configs.PRICE_SEGMENT_ALLOWED_REFERRERS)
    })
;

export { createFeatureConfigs, createFeatureSet, createServerFeatureConfigs, parseCommaSeparatedString };
//# sourceMappingURL=utils.js.map
